/**
 * How Signalements words a report: its severity, status and verdict, a
 * restriction's time (a suspension lasts until a staff decision; the review
 * deadline is not an expiry), and a refused write in the staff member's
 * language with its request ID (ADR-0016 §2.5).
 */
import type {
  SafetyReportDetail,
  SafetyReportSeverity,
  SafetyReportStatus,
  SafetyRestriction,
  SafetyVerdict,
} from "@wallyo/contracts/console/safety";
import { ConsoleApiError } from "../../api/errors";
import { describeError } from "../../auth/error-message";
import { t, type MessageId } from "../../i18n";
import { formatDateTime } from "../../i18n/format";
import type { WriteFailure } from "../team/team-format";

type Tone = "ok" | "warn" | "bad" | "info" | "mute";

export const SEVERITY_TONE: Record<SafetyReportSeverity, Tone> = {
  critical: "bad",
  high: "bad",
  medium: "warn",
  low: "mute",
};

export const STATUS_TONE: Record<SafetyReportStatus, Tone> = {
  open: "warn",
  investigating: "info",
  decided: "ok",
  appealed: "warn",
  closed: "mute",
};

export const severityName = (severity: SafetyReportSeverity) => t(`reports.severity.${severity}` as const);
export const statusName = (status: SafetyReportStatus) => t(`reports.status.${status}` as const);
export const verdictName = (verdict: SafetyVerdict) => t(`reports.verdict.${verdict}` as const);

/** The restriction still holds: not lifted, and a temporary suspension not past its end date. */
export function inForce(restriction: SafetyRestriction, now: Date): boolean {
  if (restriction.liftedAt !== null) return false;
  return restriction.expiresAt === null || Date.parse(restriction.expiresAt) > now.getTime();
}

/**
 * An interim suspension in force past its review deadline: late for staff,
 * and still in force (it never ends by itself). The server's flag, or the
 * deadline passing while the screen is open.
 */
export function reviewOverdue(restriction: SafetyRestriction, now: Date): boolean {
  if (restriction.liftedAt !== null || restriction.reviewBy === null) return false;
  return restriction.reviewOverdue || Date.parse(restriction.reviewBy) <= now.getTime();
}

/** How long it lasts, in words: never "expires" for an interim suspension or a ban. */
export function restrictionTerm(restriction: SafetyRestriction): string {
  switch (restriction.kind) {
    case "interim_suspension":
      return restriction.reviewBy
        ? t("reports.restriction.untilDecisionReviewBy", { date: formatDateTime(restriction.reviewBy) })
        : t("reports.restriction.untilDecision");
    case "temporary_suspension":
      return restriction.expiresAt
        ? t("reports.restriction.until", { date: formatDateTime(restriction.expiresAt) })
        : t("reports.restriction.untilDecision");
    case "permanent_ban":
      return t("reports.restriction.permanent");
  }
}

/** The report's in-force interim suspension, the one "Lever" lifts. */
export function interimInForce(detail: SafetyReportDetail, now: Date): SafetyRestriction | null {
  return detail.restrictions.find((each) => each.kind === "interim_suspension" && inForce(each, now)) ?? null;
}

/** A hold on the report itself, not released. */
export const activeReportHold = (detail: SafetyReportDetail) =>
  detail.legalHolds.find(
    (hold) =>
      hold.entityType === "safety_report" && hold.entityId === detail.safetyReportId && hold.releasedAt === null,
  ) ?? null;

export type ReportFailureContext = "general" | "appeal" | "share";

const REFUSED: Record<ReportFailureContext, MessageId> = {
  general: "reports.error.refused",
  appeal: "reports.error.appealRefused",
  share: "reports.error.shareRefused",
};

/** The server's reasons a report write is refused for (ConsoleErrorReason), in the staff member's words. */
const REFUSALS: Readonly<Record<string, { message: MessageId; stale: boolean }>> = {
  stale_version: { message: "reports.error.stale", stale: true },
  wrong_status: { message: "reports.refused.wrongStatus", stale: true },
  already_restricted: { message: "reports.refused.alreadyRestricted", stale: false },
  not_suspended: { message: "reports.refused.notSuspended", stale: true },
  suspended_until_required: { message: "reports.refused.suspendedUntilRequired", stale: false },
  own_verdict: { message: "reports.refused.ownVerdict", stale: false },
  owner_only: { message: "reports.error.shareRefused", stale: false },
  already_held: { message: "reports.refused.alreadyHeld", stale: true },
  not_held: { message: "reports.refused.notHeld", stale: true },
};

/**
 * A refused write (ADR-0016 §2.5), from the server's `reason` when this knows
 * it; otherwise translated by its code. Never the server's English text;
 * always the request ID. A reason saying the report moved on (a stale
 * version, another status) offers a reload.
 */
export function describeReportFailure(error: unknown, context: ReportFailureContext = "general"): WriteFailure {
  const { message, reference } = describeError(error);
  if (!(error instanceof ConsoleApiError)) return { message, reference, stale: false };
  const refusal = error.reason ? REFUSALS[error.reason] : undefined;
  if (refusal) return { message: t(refusal.message), reference, stale: refusal.stale };
  switch (error.code) {
    case "stale_version":
      return { message: t("reports.error.stale"), reference, stale: true };
    case "feature_not_enabled":
      return { message: t(REFUSED[context]), reference, stale: false };
    case "validation_error":
      return { message: t("reports.error.invalid"), reference, stale: true };
    case "network_error":
      return error.outcomeUnknown
        ? { message: t("reports.error.outcomeUnknown"), reference, stale: false }
        : { message, reference, stale: false };
    default:
      return { message, reference, stale: false };
  }
}
